// Поток партии: действия игрока, ход, барахолки, конец игры, подсчёт очков.
// Единственная точка входа для всех действий — applyAction(G, pid, action): её вызывают хотсит (напрямую),
// сервер (api.ts) и бот. Незавершённый эффект хранится в G.pending и переигрывается при загрузке.
import type { Action, ActionOutcome, CardId, CardInst, CardRef, Game, Iid, JournalEntry, ManualField, Player, PlayerId, PromptAnswer, Score, ScoreEntry, ScoreLine, SetupChoice , MutatorId } from '../types.ts';
import type { GameConfig } from './state.ts';
import {
  card, log, player, drawCards, removeFrom, gainChips, gainLimp, spendChips, trashCard, placeMarketChip, controlled, ownedCards, giveCard,
  startTurn, nextPlayerId, maxLife, setIidCounter, emit, ddkDef, ddkPenalty, turnOf, setupOf, setupDone, rngOf,
  cloneGame, overwriteGame, maxIid, makeInstance, traitDef, isBespredel, applyRebalance, createGame,
} from './state.ts';
import { catchUpOn, DUEL_TUNE, handSizeOf, hasMutator, legendMarketSizeOf, marketSizeOf, startLifeOf } from './mutators.ts';
import { RULES, CHAOS_CARD, RULES_VERSION, RULES_VERSION_MIN, rv, reb, stepwiseSetup, chaosToHand, familiarToHand, holdForTableTrash, holdRevealsForTrash, sharedWin, stepUndo } from './rules.ts';
import { shuffle, randInt } from './rng.ts';
import * as E from './effects.ts';
import type { Eff } from './effects.ts';
import * as H from './hooks.ts';
import * as T from './tokens.ts';

// ---------- раннер генераторов ----------
// Живой генератор привязан к объекту состояния: копии состояния (снимки, «примерки» бота) друг другу не мешают.
const gens = new WeakMap<Game, Eff<unknown>>();

/**
 * Снимок для «шага назад»: состояние до последнего ЗАВЕРШЁННОГО действия игрока. Живёт вне партии — в
 * сохранение и в сетевой трафик не попадает. Ключ — сама партия (сид + число игроков), а не объект
 * состояния: онлайн-движок пересоздаёт объект на каждом запросе (api.load), да и хотсит после
 * возобновления партии строит новый, — иначе кнопка отмены пропадала бы ровно там, где она нужнее всего.
 * Позиция в журнале (at) привязывает снимок к конкретному действию: любое следующее действие его обесценит.
 */
interface UndoEntry {
  base: Game;
  pid: PlayerId;
  what: string;
  at: number;
  lockSeq: number;
  rngS: number;
  /** счётчик раскрытий СВОИХ карт на момент снимка: если он с тех пор вырос, действие уже показало игроку
   *  его карту (например, «раскрой верх колоды: уничтожить или взять мощь») — такое действие целиком не
   *  откатывается, но последний выбор переигрывается: карта остаётся открытой, вопрос задаётся заново */
  softSeq: number;
  /** снимок сделан не перед действием игрока, а перед авто-розыгрышем простых карт сразу после него */
  auto?: true;
  /** авто-розыгрыш после действия игрока (с v20): точка отката самого действия — следующий шаг «Отмены» */
  prev?: UndoEntry;
}
const undos = new Map<string, UndoEntry>();
/** Сколько партий помним (процесс сервера живёт долго; снимок тяжёлый — держим ограниченный запас) */
const UNDO_LIMIT = 64;
const undoKey = (G: Game): string => `${G.seed ?? 0}:${G.players.length}`;
function undoSet(G: Game, e: UndoEntry): void {
  const key = undoKey(G);
  undos.delete(key);
  undos.set(key, e);
  if (undos.size > UNDO_LIMIT) {
    const oldest = undos.keys().next().value;
    if (oldest !== undefined) undos.delete(oldest);
  }
}
/** Кандидат в точки отката: действие доиграно, но в журнал ещё не записано (позиция станет известна в applyAction) */
let pendingUndo: Omit<UndoEntry, 'at'> | null = null;
/**
 * Отменён авто-розыгрыш после действия игрока (с v20): следующим шагом «Отмены» станет само действие. Метка — из
 * восстановленного снимка, снимок — из кэша (если был); позицию в журнале (запись отмены) назначит applyAction
 */
let restoreUndo: { mark: NonNullable<Game['undoAt']>; base: Game | null } | null = null;

/**
 * Снимок состояния до последнего действия — чтобы сохранить его вместе с партией. Реплей журнала как
 * запасной путь ненадёжен: он требует ровно той версии правил, на которой партия игралась, а правила
 * между обновлениями меняются. Сохранённый снимок работает всегда.
 */
export function undoSnapshot(G: Game): Game | null {
  const mark = G.undoAt;
  const u = undos.get(undoKey(G));
  return mark && u && u.at === mark.at ? u.base : null;
}

/** Вернуть снимок в кэш процесса при загрузке партии (см. undoSnapshot) */
export function primeUndo(G: Game, base: Game): void {
  const mark = G.undoAt;
  if (!mark) return;
  undoSet(G, { base, pid: mark.pid, what: mark.what, at: mark.at, lockSeq: mark.lockSeq, rngS: mark.rngS, softSeq: mark.softSeq });
}

/** Забыть кэш снимков процесса (тесты: имитация перезапуска приложения) */
export function forgetUndoCache(): void {
  undos.clear();
  pendingUndo = null;
}

/**
 * Восстановить метку «шага назад» по журналу: партия могла быть сохранена версией, которая метку ещё не
 * писала (или прийти с другого устройства). Зовётся один раз при продолжении партии — реплей журнала дорог,
 * поэтому в общем пути загрузки (restoreRuntime, в том числе на сервере) этого не делаем.
 */
export function recoverUndo(G: Game): void {
  // legacy — реплей уже пробовали и он не сошёлся; до следующего действия пробовать заново бессмысленно
  if (G.undoAt || G.undoWhy === 'legacy' || G.phase !== 'play' || G.pending || G.prompt || !G.turn) return;
  const journal = G.journal ?? [];
  if (G.seed == null || !journal.length) return;
  // последнее действие игрока — это карта/покупка плюс его ответы на её вопросы; идём с конца через ответы
  let start = journal.length - 1;
  while (start >= 0 && journal[start]!.a.name === 'answer') {
    if (journal[start]!.pid !== G.turn.player) return; // отвечал кто-то другой — откат закрыт
    start--;
  }
  const act = journal[start];
  if (!act || !CANCELLABLE.has(act.a.name) || act.pid !== G.turn.player) return;
  const spec: ReplaySpec = { seed: G.seed, players: replayPlayers(G), ...gameFlags(G), journal };
  const upTo = replayJournal(spec, rv(G), start);
  if (upTo.failAt >= 0) {
    // партия игралась на правилах, которых в истории версий нет: журнал не воспроизводится. Это не
    // «нечего отменять» — действие есть, но снимка к нему нет; игроку говорим именно это
    G.undoWhy = 'legacy';
    return;
  }
  const base = cloneGame(upTo.G);
  // счётчики раскрытий сверяем между снимком и концом журнала, посчитанными ОДНИМ движком: в сохранении
  // старой версии их может не быть вовсе, а реплей проверен (ensureRulesVersion) — его счёт и есть истина
  const full = upTo.G;
  for (let i = start; i < journal.length; i++) {
    const e = journal[i];
    if (!e || !applyEntry(full, e).ok) {
      G.undoWhy = 'legacy';
      return;
    }
  }
  if (G.lockSeq == null) G.lockSeq = full.lockSeq ?? 0;
  if (G.softSeq == null) G.softSeq = full.softSeq ?? 0;
  // действие можно отменить, только если оно не раскрыло чужого и не вовлекло других игроков
  if ((base.lockSeq ?? 0) !== (full.lockSeq ?? 0)) {
    G.undoWhy = 'foreign';
    return;
  }
  const entry = { base, pid: act.pid, what: actionLabel(act.a), at: journal.length, lockSeq: base.lockSeq ?? 0, rngS: base.rng?.s ?? 0, softSeq: base.softSeq ?? 0 };
  undoSet(G, entry);
  G.undoAt = { at: entry.at, lockSeq: entry.lockSeq, rngS: entry.rngS, softSeq: entry.softSeq, pid: entry.pid, what: entry.what };
}

/** Восстановить состояние до действия журнала №at: сначала из кэша процесса, иначе реплеем журнала */
function undoBase(G: Game, mark = G.undoAt): Game | null {
  if (!mark) return null;
  const cached = undos.get(undoKey(G));
  // у авто-розыгрыша поверх действия позиция та же, что у действия: снимок действия — в prev
  if (cached && cached.at === mark.at && !(cached.auto && !mark.auto)) return cached.base;
  if (cached?.prev && !mark.auto && cached.prev.at === mark.at) return cached.prev.base;
  const journal = G.journal ?? [];
  // позиция самого действия: после отмены авто-розыгрыша (v20) журнал длиннее на запись отмены
  const src = mark.src ?? mark.at;
  if (src > journal.length || G.seed == null) return null;
  // реплей до предыдущего действия: партия детерминирована сидом, журналом и версией правил
  const r = replayJournal({ seed: G.seed, players: replayPlayers(G), ...gameFlags(G), journal }, rv(G), src - 1);
  if (r.failAt >= 0) return null;
  if (!mark.auto) return r.G;
  // снимок авто-розыгрыша — состояние ПОСЛЕ действия, но ДО автоматики; в журнале автоматики нет,
  // поэтому само действие доигрываем с выключенной автоматикой
  const e = journal[src - 1];
  if (!e) return null;
  noAutoOnce = true;
  try {
    return applyEntry(r.G, e).ok ? r.G : null;
  } finally {
    noAutoOnce = false;
  }
}
/** Идёт автоматика (Знаки, авто-активации): её розыгрыши игрок не нажимал — точкой отката они не становятся */
let autoPlaying = false;
/** Выключить автоматику ровно на одно действие: так undoBase восстанавливает состояние до авто-розыгрыша */
let noAutoOnce = false;

/** Человеческое имя действия для сообщения об откате */
function actionLabel(a: Action): string {
  switch (a.name) {
    case 'playCard': return 'розыгрыш карты';
    case 'activateCard': return 'активацию';
    case 'buyCard': case 'buyChaos': return 'покупку';
    default: return 'действие';
  }
}

function run(G: Game, gen: Eff<unknown>): void {
  gens.set(G, gen);
  step(G, undefined);
}

function step(G: Game, ans: PromptAnswer | undefined): void {
  const gen = gens.get(G);
  if (!gen) return;
  let r: IteratorResult<Game['prompt'], unknown>;
  try {
    // первый next() генератора всегда без аргумента
    r = ans === undefined ? gen.next() : gen.next(ans);
  } catch (e) {
    console.error(e);
    log(G, `⚠ Ошибка эффекта: ${e instanceof Error ? e.message : String(e)}`);
    gens.delete(G);
    G.prompt = null;
    G.pending = null;
    return;
  }
  if (r.done) {
    gens.delete(G);
    const done = G.pending;
    G.prompt = null;
    G.pending = null;
    // действие доиграно: если оно ничего не раскрыло и никто другой не решал — его ещё можно отменить целиком.
    // Автоматика (Знаки, авто-активации) точку отката не трогает: её ставит только нажатие игрока
    if (!autoPlaying) {
      const ok = done && CANCELLABLE.has(done.action.name) && clean(G, done.base);
      pendingUndo = ok && done
        ? { base: done.base, pid: done.pid, what: actionLabel(done.action), lockSeq: done.base.lockSeq ?? 0, rngS: done.base.rng?.s ?? 0, softSeq: done.base.softSeq ?? 0 }
        : null;
      // запоминаем, почему точки отката не будет: догадываться об этом задним числом нельзя
      if (!ok) G.undoWhy = done && CANCELLABLE.has(done.action.name) ? 'foreign' : 'plain';
      else delete G.undoWhy;
      undos.delete(undoKey(G)); // прежний снимок устарел: между ним и текущим моментом было действие
    }
    if (G.instantWin != null && G.phase === 'play') {
      gens.set(G, finishGame(G, G.instantWin));
      step(G, undefined);
    }
  } else {
    G.prompt = r.value;
  }
}

export function busy(G: Game): boolean {
  return gens.has(G);
}

/** Ответ на активный запрос */
function answer(G: Game, value: PromptAnswer): void {
  if (!gens.has(G)) return;
  // мотив бота — именно здесь, а не в dispatch: восстановление (restoreRuntime) переигрывает ответы
  // через answer() напрямую, и заметка должна воспроизводиться, иначе состояния разойдутся
  const who = promptWho(G);
  if (who != null) botTargetNote(G, who, value);
  const pending = G.pending;
  if (pending) {
    pending.answers.push(value);
    // решение принял другой игрок — откатывать его выбор нельзя
    if (promptWho(G) !== pending.pid) {
      pending.irreversible = true;
      G.lockSeq = (G.lockSeq ?? 0) + 1;
    }
  }
  step(G, value);
}

/**
 * Запустить эффект, порождённый действием. base — снимок состояния до первой мутации действия:
 * переигрывание = applyAction(base, action) + те же ответы.
 */
function runAction(G: Game, base: Game, pid: PlayerId, action: Action, gen: Eff<unknown>): void {
  G.pending = { base, pid, action, answers: [], irreversible: false };
  run(G, gen);
}

/** Действия, которые игрок может отменить, пока эффект ждёт его же решения */
const CANCELLABLE: ReadonlySet<Action['name']> = new Set(['playCard', 'activateCard', 'buyCard', 'buyChaos']);

/**
 * С момента снимка не случилось ничего, что откат сделал бы нечестным: не раскрыты чужие карты и никто
 * другой не принимал решений (счётчик lockSeq). Расход случайности здесь не помеха: откат возвращает и
 * состояние ГСЧ, поэтому повтор того же действия даст тот же результат — «переиграть неудачу» нельзя.
 */
function clean(G: Game, base: Game): boolean {
  return (G.lockSeq ?? 0) === (base.lockSeq ?? 0);
}

/**
 * Может ли игрок отменить своё незавершённое действие: эффект ждёт решения от него самого, никто другой ещё
 * ничего не решал, скрытой информации не раскрыто и случайность не расходовалась (иначе откат = подглядывание).
 */
export function canCancel(G: Game, pid: PlayerId): boolean {
  const pending = G.pending;
  if (!pending || !G.prompt || pending.pid !== pid || promptWho(G) !== pid) return false;
  if (pending.irreversible || !CANCELLABLE.has(pending.action.name)) return false;
  if (G.rng && pending.base.rng && G.rng.s !== pending.base.rng.s) return false;
  return true;
}

/**
 * Шаг назад: отменить уже ЗАВЕРШЁННОЕ последнее действие своего хода (нечаянно уничтожил карту вместо того,
 * чтобы взять её мощь). Доступен, только пока действие ничего не раскрыло: любой добор, раскрытие верха
 * колоды, чужое решение (защита) или расход случайности закрывают откат — иначе отмена стала бы подглядыванием.
 */
export function canUndo(G: Game, pid: PlayerId): boolean {
  if (G.phase !== 'play' || G.pending || G.prompt) return false;
  if (G.turn?.player !== pid) return false;
  const mark = G.undoAt;
  if (!mark || mark.pid !== pid || mark.at !== (G.journal?.length ?? 0)) return false;
  // с момента снимка не раскрылось чужого и никто другой не решал (случайность откат возвращает сам)
  if ((G.lockSeq ?? 0) !== mark.lockSeq) return false;
  // если действие показало игроку его собственную карту, целиком отменить нельзя — только переиграть
  // последний выбор, а для этого он должен существовать (у «возьми 2 карты» выбора нет)
  if ((G.softSeq ?? 0) !== mark.softSeq) {
    if (mark.auto) return false; // у авто-розыгрыша нет «последнего выбора», который можно переиграть
    const last = (G.journal ?? [])[(G.journal?.length ?? 0) - 1];
    return !!last && last.a.name === 'answer' && last.pid === pid;
  }
  return true;
}

/**
 * Почему «шаг назад» сейчас недоступен — понятной игроку фразой (null, если доступен). Молчаливо
 * отсутствующая кнопка выглядит как поломка; причина объясняет правило: отмена закрывается всем, что
 * раскрывает карты или вовлекает других игроков.
 */
export function undoReason(G: Game, pid: PlayerId): string | null {
  if (canCancel(G, pid) || canUndo(G, pid)) return null;
  if (G.phase !== 'play') return 'партия не идёт';
  if (G.turn?.player !== pid) return 'сейчас не твой ход';
  if (G.prompt || G.pending) return 'сначала ответь на вопрос';
  const mark = G.undoAt;
  if (!mark) {
    if (G.undoWhy === 'foreign') return 'в этом действии кто-то ещё принимал решение или раскрылись чужие карты';
    if (G.undoWhy === 'legacy') return 'это действие сделано до обновления игры — вернуть его уже нельзя, следующие можно будет';
    if (G.undoWhy === 'autodraw') return 'автоматика добрала карты — вернуть розыгрыш значило бы подглядеть в колоду';
    return 'в этом ходу отменять нечего';
  }
  if (mark.pid !== pid) return 'последнее действие — не твоё';
  if (mark.at !== (G.journal?.length ?? 0)) return 'после него были другие действия';
  if ((G.softSeq ?? 0) !== mark.softSeq) return 'ты уже взял карты — переигрывать нечего';
  return 'ты увидел чужие карты — отмена дала бы подглядеть';
}

/** Что именно откатит кнопка отмены (для подписи) — null, если откатывать нечего */
export function undoLabel(G: Game, pid: PlayerId): string | null {
  if (canCancel(G, pid)) {
    const a = G.pending?.action;
    return a ? actionLabel(a) : 'действие';
  }
  if (!canUndo(G, pid)) return null;
  const mark = G.undoAt;
  // карта уже раскрыта — предлагаем переиграть выбор, а не всё действие
  if (mark && (G.softSeq ?? 0) !== mark.softSeq) return 'выбор (карта останется открытой)';
  return mark?.what ?? null;
}

/**
 * Отменить своё действие: незавершённое (эффект ждёт решения) или уже завершённое последнее действие хода
 * («шаг назад» — см. canUndo).
 *
 * Если действие успело показать игроку его собственную карту («раскрой верх колоды: уничтожить или взять
 * мощь»), целиком оно не откатывается — иначе игрок переигрывал бы, уже зная карту. Вместо этого
 * переигрывается его последний ВЫБОР: состояние возвращается к тому же вопросу с той же открытой картой.
 */
function cancel(G: Game, pid: PlayerId, stepAsked = false): ActionOutcome {
  const pending = G.pending;
  const cancelling = canCancel(G, pid);
  if (!cancelling && !canUndo(G, pid)) return fail('отменить уже нельзя');
  let mark = G.undoAt;
  const journal = G.journal;
  // отменяется авто-розыгрыш после действия игрока: снимок — состояние сразу после действия, и в нём уже стоит
  // метка самого действия; по шагам (интерфейс, v20) она станет следующим шагом «Отмены» (снимок — из кэша, иначе —
  // реплеем по журналу). Отмена без пометки в партии до v20 (старый журнал) — ровно как тогда: авто-розыгрыш
  // отдельной точкой не был, отменялось само действие (целиком или переигрышем последнего выбора)
  const prevEntry = !cancelling && mark?.auto ? undos.get(undoKey(G))?.prev ?? null : null;
  let autoStep = !cancelling && !!mark?.auto;
  if (autoStep && mark && !(stepAsked || stepUndo(G))) {
    const act = undoBase(G)?.undoAt;
    if (act && !act.auto && act.pid === pid && act.at === mark.at) {
      mark = act;
      autoStep = false;
    }
  }
  let base = cancelling ? pending?.base : undoBase(G, mark);
  let what = cancelling ? (pending ? actionLabel(pending.action) : 'действие') : mark?.what ?? 'действие';
  // раскрыта своя карта — переигрываем только последний выбор внутри уже начатого действия
  let replay: Game['pending'] = null;
  if (!cancelling && mark && base && (G.softSeq ?? 0) !== mark.softSeq) {
    const all = journal ?? [];
    let start = all.length - 1;
    while (start >= 0 && all[start]!.a.name === 'answer') start--;
    const act = all[start];
    const answers = all.slice(start + 1).map((e) => e.a.args[0] as PromptAnswer);
    if (!act || !answers.length) return fail('это решение уже не переиграть');
    replay = { base: cloneGame(base), pid, action: act.a, answers: answers.slice(0, -1), irreversible: false };
    what = 'выбор';
  }
  if (!base) return fail('нечего отменять');
  const seq = G.eventSeq;
  gens.delete(G);
  overwriteGame(G, cloneGame(base));
  if (replay) {
    // переигрываем действие и все ответы, кроме последнего, ПРЯМО на этом состоянии: живой генератор
    // эффекта привязан к объекту партии, поэтому подменять объект здесь нельзя
    const r = dispatch(G, replay.pid, replay.action);
    if (!r.ok) return fail('это решение уже не переиграть');
    for (const ans of replay.answers) answer(G, ans);
  }
  if (!replay) {
    // при переигровке выбора вопрос остаётся активным — гасим его только при полном откате действия
    G.prompt = null;
    G.pending = null;
  }
  // отменён авто-розыгрыш: вернувшиеся в руку карты автоматика больше не играет — иначе она сыграла бы их
  // тут же снова. Дальше игрок решает сам: сыграть вручную и в своём порядке или оставить в руке
  if (autoStep && G.turn) {
    const hand = player(G, G.turn.player).hand.filter((c) => T.isAutoPlay(c.cid)).map((c) => c.iid);
    G.turn.noAuto = [...new Set([...(G.turn.noAuto ?? []), ...hand])];
  }
  // метку из снимка не трогаем: состояние после отмены обязано совпадать с ним побитово (это проверяет sim).
  // Повторный откат всё равно закрыт — сама отмена уходит в журнал, и позиция метки перестаёт совпадать
  undos.delete(undoKey(G));
  restoreUndo = null;
  const back = G.undoAt;
  if (autoStep && back && !back.auto && back.pid === pid && back.at === (journal?.length ?? 0)) {
    restoreUndo = { mark: back, base: prevEntry && prevEntry.at === back.at ? prevEntry.base : null };
  }
  G.eventSeq = Math.max(G.eventSeq, seq);
  G.journal = journal; // журнал не откатывается: отменённое действие и сама отмена остаются (для воспроизведения) // нумерация событий монотонна и после отката: UI отличит новое от виденного
  const p = player(G, pid);
  log(G, `↩ ${p.name} отменяет ${what}`);
  emit(G, { type: 'note', pid, text: `отменяет ${what}` });
  return { ok: true };
}

// ---------- подготовка ----------

/**
 * Выбор жетона свойства. С v11 — по правилам (стр. 4): жетон выбирается первым, ещё не видя фамильяров
 * (choice.familiar должен быть null), фамильяр — отдельным действием setupFamiliar. До v11 — жетон и фамильяр
 * одним выбором. При «трёх фамильярах» выбирать фамильяра не нужно: остаются оба предложенных (третий — во второй фазе)
 */
export function setupChoose(G: Game, pid: PlayerId, choice: SetupChoice): ActionOutcome {
  if (G.phase !== 'setup') return fail('не фаза подготовки');
  const p = player(G, pid);
  if (p.traitId) return fail('уже выбрано');
  const setup = setupOf(G);
  const offers = setup.traitOffers[pid] ?? [];
  const famOffers = setup.familiarOffers[pid] ?? [];
  if (!offers.includes(choice.traitId)) return fail('недопустимый жетон');
  const trait = traitDef(choice.traitId);
  const base = cloneGame(G);
  const three = !!H.traitPassive({ ...p, traitId: trait.id })?.threeFamiliars;
  if (stepwiseSetup(G)) {
    if (!three && choice.familiar != null) return fail('сначала жетон свойства, фамильяр — после');
    p.traitId = trait.id;
    if (three) takeFamiliars(p, [...famOffers]);
    return setupProgress(G, base, pid, { name: 'setupChoose', args: [choice] });
  }
  let fams: string[];
  if (three) {
    fams = [...famOffers];
  } else {
    if (!choice.familiar || !famOffers.includes(choice.familiar)) return fail('недопустимый фамильяр');
    fams = [choice.familiar];
    // невыбранный фамильяр — ничейный: его может взять третьим владелец жетона «три фамильяра»
    setup.freeFamiliars.push(...famOffers.filter((cid) => cid !== choice.familiar));
  }
  p.traitId = trait.id;
  takeFamiliars(p, fams);
  return setupProgress(G, base, pid, { name: 'setupChoose', args: [choice] });
}

/** Выбор фамильяра (с v11): после жетона свойства, один из двух предложенных */
export function setupFamiliar(G: Game, pid: PlayerId, cid: CardId): ActionOutcome {
  if (G.phase !== 'setup') return fail('не фаза подготовки');
  if (!stepwiseSetup(G)) return fail('в этой версии правил фамильяр выбирается вместе с жетоном');
  const p = player(G, pid);
  if (!p.traitId) return fail('сначала выбери жетон свойства');
  if (setupDone(p)) return fail('уже выбрано');
  const setup = setupOf(G);
  const famOffers = setup.familiarOffers[pid] ?? [];
  if (!famOffers.includes(cid)) return fail('недопустимый фамильяр');
  const base = cloneGame(G);
  takeFamiliars(p, [cid]);
  // невыбранный фамильяр — ничейный: его может взять третьим владелец жетона «три фамильяра»
  setup.freeFamiliars.push(...famOffers.filter((x) => x !== cid));
  return setupProgress(G, base, pid, { name: 'setupFamiliar', args: [cid] });
}

function takeFamiliars(p: Player, fams: readonly CardId[]): void {
  p.familiars = fams.map((cid) => makeInstance(cid));
  // колдун-планшет ни на что не влияет — берём первого от выбранного фамильяра
  const first = fams[0];
  p.wizard = first ? card(first).familiarOf[0] ?? '' : '';
}

/** Все закончили первую фазу — вторая (третьи фамильяры) или начало партии */
function setupProgress(G: Game, base: Game, pid: PlayerId, action: Action): ActionOutcome {
  if (!G.players.every(setupDone)) return { ok: true };
  const setup = setupOf(G);
  // ничейные известны только теперь — владельцы «трёх фамильяров» выбирают третьего по очереди
  const need = G.players.filter((x) => H.traitPassive(x)?.threeFamiliars).map((x) => x.id);
  if (need.length && setup.freeFamiliars.length) setup.third = need;
  else {
    finishSetup(G);
    runAction(G, base, pid, action, beginTurn(G));
  }
  return { ok: true };
}

/** Вторая фаза подготовки: третий фамильяр из ничейных (null — только если ничейных не осталось) */
export function setupThird(G: Game, pid: PlayerId, cid: CardId | null): ActionOutcome {
  if (G.phase !== 'setup') return fail('не фаза подготовки');
  const setup = setupOf(G);
  const third = setup.third ?? [];
  if (!third.includes(pid)) return fail('сейчас не твой выбор');
  if (cid == null && setup.freeFamiliars.length) return fail('выбери третьего фамильяра из ничейных');
  if (cid != null && !setup.freeFamiliars.includes(cid)) return fail('недопустимый третий фамильяр');
  const base = cloneGame(G);
  const p = player(G, pid);
  if (cid != null) {
    p.familiars.push(makeInstance(cid));
    setup.freeFamiliars = setup.freeFamiliars.filter((c) => c !== cid);
  }
  setup.third = third.filter((x) => x !== pid);
  if (!setup.third.length) {
    finishSetup(G);
    runAction(G, base, pid, { name: 'setupThird', args: [cid] }, beginTurn(G));
  }
  return { ok: true };
}

function finishSetup(G: Game): void {
  const rng = rngOf(G);
  // стартовые колоды и барахолки
  for (const p of G.players) {
    const t = H.traitPassive(p);
    const starter = t?.starterDeck ? t.starterDeck(makeInstance) : T.defaultStarterDeck();
    // «Наследство»: верхняя карта основной колоды не дороже 6 (беспределы пропускаются) — в стартовую колоду
    if (hasMutator(G, 'heritage')) {
      for (let i = G.mainDeck.length - 1; i >= 0; i--) {
        const c = G.mainDeck[i]!;
        if (isBespredel(c) || (card(c).cost ?? 0) > 6) continue;
        G.mainDeck.splice(i, 1);
        starter.push(c);
        log(G, `${p.name}: наследство — «${card(c).name}»`);
        break;
      }
    }
    p.deck = shuffle(rng, starter);
    p.life = H.baseLife(G, p, startLifeOf(G));
    if (t?.startsWithPrize && (t.startsWithPrizeIn?.(G) ?? true)) G.prizeOwner = p.id;
    drawCards(G, p, H.handLimit(G, p, handSizeOf(G)));
  }
  // барахолка: беспределы из начальной раскладки уничтожаются
  while (G.market.length < marketSizeOf(G)) {
    const c = G.mainDeck.pop();
    if (!c) break;
    if (isBespredel(c)) G.trashBespredel.push(c);
    else G.market.push(c);
  }
  while (G.legendMarket.length < legendMarketSizeOf(G)) {
    const c = G.legendDeck.pop();
    if (!c) break;
    if (isBespredel(c)) G.trashBespredel.push(c);
    else G.legendMarket.push(c);
  }
  for (const c of [...G.market, ...G.legendMarket]) placeMarketChip(G, c);
  // первый игрок: владелец приза, иначе случайный
  // в режиме ребаланса (с версии 9) свойство «приз» не даёт права первого хода (см. rebalancedTrait('prize'))
  const first = (reb(G, 9) ? null : G.prizeOwner) ?? randInt(rng, G.players.length);
  G.phase = 'play';
  G.setup = null;
  startTurn(G, first);
}

// ---------- ход ----------

function* beginTurn(G: Game): Eff {
  const turn = turnOf(G);
  const p = player(G, turn.player);
  yield* refillMarkets(G, p);
  // эффекты «в начале хода»
  H.bossStartOfTurn(G, p);
  // отстающий от всех берёт карту: пассивка босса (Танатыч) и мутаторы «Догоняющий»/«Дуэль» — складываются
  let catchUps = (H.catchUpDraw(p) ? 1 : 0) + (catchUpOn(G) ? 1 : 0);
  if (catchUps) {
    const mine = scorePlayer(G, p).total;
    const gap = Math.min(...G.players.filter((r) => r.id !== p.id).map((r) => scorePlayer(G, r).total)) - mine;
    if (hasMutator(G, 'duel') && gap >= DUEL_TUNE.bigGap) catchUps++;
    if (gap > 0) {
      drawCards(G, p, catchUps);
      log(G, `${p.name} догоняет: берёт ${catchUps === 1 ? 'карту' : `${catchUps} карты`}`);
    }
  }
  if (H.limpToLeader(p) && Math.floor(((G.turnNo ?? 1) - 1) / G.players.length) % 2 === 1) {
    // каждый второй круг: враг-лидер по очкам (при равенстве — первый по часовой от босса) получает вялую палочку
    let best: Player | null = null;
    for (let k = 1; k < G.players.length; k++) {
      const r = player(G, (p.id + k) % G.players.length);
      if (!best || scorePlayer(G, r).total > scorePlayer(G, best).total) best = r;
    }
    if (best && scorePlayer(G, best).total > scorePlayer(G, p).total && gainLimp(G, best)) log(G, `${p.name}: ${best.name} получает вялую палочку`);
  }
  yield* H.startOfTurn(G, p);
  // освежить кошелёк постоянок (+N мощи постоянок)
  const pp = H.startPower(G, p);
  if (pp) {
    turn.power += pp;
    log(G, `⚡ +${pp} мощи (постоянки)`);
  }
  turn.needsRefill = false;
}

export function* refillMarkets(G: Game, p: Player): Eff {
  // «Беспредельный круг»: в начале каждого круга со второго — ближайший к верху беспредел основной колоды
  if (hasMutator(G, 'bespredelRound') && (G.turnNo ?? 1) > G.players.length && ((G.turnNo ?? 1) - 1) % G.players.length === 0) {
    for (let i = G.mainDeck.length - 1; i >= 0; i--) {
      const c = G.mainDeck[i]!;
      if (card(c).type !== 'беспредел') continue;
      G.mainDeck.splice(i, 1);
      const def = card(c);
      log(G, `БЕСПРЕДЕЛЬНЫЙ КРУГ! ${def.text}`);
      emit(G, { type: 'bespredel', iid: c.iid, cid: c.cid, mega: false });
      yield* E.info(G, null, 'БЕСПРЕДЕЛЬНЫЙ КРУГ!', def.text, { cards: [E.cardRef(c)] });
      yield* T.playBespredel(G, p, c);
      trashCard(G, c);
      break;
    }
  }
  // обычная барахолка
  while (G.market.length < marketSizeOf(G)) {
    const c = G.mainDeck.pop();
    if (!c) {
      G.endTriggered = true;
      log(G, 'Основная колода кончилась — барахолку не пополнить: игра закончится в конце этого хода');
      break;
    }
    const def = card(c);
    if (def.type === 'беспредел') {
      log(G, `БЕСПРЕДЕЛ! ${def.text}`);
      emit(G, { type: 'bespredel', iid: c.iid, cid: c.cid, mega: false });
      yield* E.info(G, null, 'БЕСПРЕДЕЛ!', def.text, { cards: [E.cardRef(c)] });
      yield* T.playBespredel(G, p, c);
      trashCard(G, c);
      continue;
    }
    G.market.push(c);
    placeMarketChip(G, c);
  }
  while (G.legendMarket.length < legendMarketSizeOf(G)) {
    const c = G.legendDeck.pop();
    if (!c) {
      G.endTriggered = true;
      log(G, 'Колода легенд кончилась — легендарную барахолку не пополнить: игра закончится в конце этого хода');
      break;
    }
    const def = card(c);
    if (def.type === 'мегабеспредел') {
      log(G, `МЕГА БЕСПРЕДЕЛ! ${def.text}`);
      emit(G, { type: 'bespredel', iid: c.iid, cid: c.cid, mega: true });
      yield* E.info(G, null, 'МЕГА БЕСПРЕДЕЛ!', def.text, { cards: [E.cardRef(c)] });
      yield* T.playBespredel(G, p, c);
      trashCard(G, c);
      continue;
    }
    G.legendMarket.push(c);
    placeMarketChip(G, c);
  }
}

const fail = (error: string): ActionOutcome => ({ ok: false, error });

/** Активный игрок, если сейчас можно действовать (ход идёт, запросов нет) */
function activePlayerFor(G: Game, pid: PlayerId): Player | ActionOutcome {
  if (G.phase !== 'play' || !G.turn) return fail('партия не идёт');
  if (G.prompt || busy(G)) return fail('сначала ответь на запрос');
  if (G.turn.player !== pid) return fail('не твой ход');
  return player(G, pid);
}
const isOutcome = (x: Player | ActionOutcome): x is ActionOutcome => 'ok' in x;

/** Сыграть карту с руки */
function playCard(G: Game, pid: PlayerId, iid: Iid): ActionOutcome {
  const p = activePlayerFor(G, pid);
  if (isOutcome(p)) return p;
  const turn = turnOf(G);
  const c = p.hand.find((x) => x.iid === iid);
  if (!c) return fail('такой карты нет на руке');
  const base = cloneGame(G);
  removeFrom(p.hand, iid);
  const def = card(c);
  if (def.permanent) p.permanents.push(c);
  else p.inPlay.push(c);
  turn.played.push(c.iid);
  log(G, `${p.name} играет «${def.name}»`);
  emit(G, { type: 'play', pid: p.id, iid: c.iid, cid: c.cid });
  runAction(G, base, pid, { name: 'playCard', args: [iid] }, T.playCardEffect(G, p, c, {}));
  return { ok: true };
}

/** Применить свойство активации ☀ карты под контролем (iid) или жетона (строковый id) */
function activateCard(G: Game, pid: PlayerId, iid: Iid | string): ActionOutcome {
  const p = activePlayerFor(G, pid);
  if (isOutcome(p)) return p;
  const turn = turnOf(G);
  if (typeof iid === 'string') {
    // активация жетона (свойства / ЖДК)
    const run_ = H.tokenActivationRun(p, iid);
    if (!run_) return fail('нет такого жетона');
    const a = H.tokenActivations(G, p).find((x) => x.id === iid);
    if (!a) return fail('жетон уже применён');
    if (a.enabled === false) return fail(a.hint ?? 'условие не выполнено');
    const base = cloneGame(G);
    if (iid.startsWith('trait')) turn.activated.push('trait');
    log(G, `${p.name} активирует жетон`);
    emit(G, { type: 'activate', pid: p.id, token: iid });
    runAction(G, base, pid, { name: 'activateCard', args: [iid] }, run_(G, p));
    return { ok: true };
  }
  const c = controlled(G, p).find((x) => x.iid === iid);
  if (!c) return fail('карта не под контролем');
  const fx = T.ACTIVATIONS[card(c).id];
  if (!fx) return fail('у карты нет активации');
  if (turn.activated.includes(iid)) return fail('уже применено в этом ходу');
  if (!canActivate(G, c)) return fail('условие активации не выполнено'); // ☀ не тратится
  const base = cloneGame(G);
  turn.activated.push(iid);
  log(G, `${p.name} активирует «${card(c).name}»`);
  emit(G, { type: 'activate', pid: p.id, iid: c.iid, cid: c.cid });
  runAction(G, base, pid, { name: 'activateCard', args: [iid] }, fx(G, p, c));
  return { ok: true };
}

export function canActivate(G: Game, c: CardInst): boolean {
  if (!G.turn) return false;
  const id = card(c).id;
  if (!T.ACTIVATIONS[id] || G.turn.activated.includes(c.iid)) return false;
  const cond = T.CAN_ACTIVATE[id];
  return !cond || cond(G, player(G, G.turn.player), c);
}

/** Стоимость карты барахолки (или фамильяра под планшетом) для игрока, с учётом скидок */
export function priceFor(G: Game, p: Player, c: CardRef): number {
  const def = card(c);
  const isFamiliar = def.type === 'фамильяр';
  const legendLike = def.legend || (isFamiliar && H.familiarsAreLegends(p));
  let cost = isFamiliar ? RULES.familiarPrice : def.cost ?? 0;
  if (legendLike && G.turn) cost = Math.max(0, cost - G.turn.legendDiscount);
  cost = Math.max(0, cost - H.discount(p, def));
  return cost;
}

/** Можно ли оплатить карту чипсинами (легенды и «фамильяры-легенды»): 1 чипсина = 1 мощь */
export function chipsPayable(p: Player, c: CardRef): boolean {
  const def = card(c);
  return def.legend || (def.type === 'фамильяр' && H.familiarsAreLegends(p));
}

/** Купить карту с барахолки или своего фамильяра (по iid) */
function buyCard(G: Game, pid: PlayerId, iid: Iid, payChips: number): ActionOutcome {
  const p = activePlayerFor(G, pid);
  if (isOutcome(p)) return p;
  const turn = turnOf(G);
  const inMarket = [...G.market, ...G.legendMarket].find((x) => x.iid === iid);
  const familiar = p.familiars.find((x) => x.iid === iid);
  const c = inMarket ?? familiar;
  if (!c) return fail('такой карты нет на барахолке');
  const def = card(c);
  const cost = priceFor(G, p, c);
  const chips = chipsPayable(p, c) ? Math.min(payChips, p.chips, cost) : 0;
  const needPower = cost - chips;
  if (turn.power < needPower) return fail('не хватает мощи');
  const base = cloneGame(G);
  turn.power -= needPower;
  spendChips(G, p, chips, `покупка «${def.name}»`);
  turn.bought.push(c.iid);
  if (familiar) {
    removeFrom(p.familiars, c.iid);
    log(G, `${p.name} покупает своего фамильяра «${def.name}» за ${cost}${chips ? ` (${chips} чипсинами)` : ''}`);
    emit(G, { type: 'buy', pid: p.id, iid: c.iid, cid: c.cid, cost, chips, pile: 'familiar' });
    runAction(G, base, pid, { name: 'buyCard', args: [iid, payChips] }, E.gainWithTriggers(G, p, c, { bought: familiarToHand(G) }));
    return { ok: true };
  }
  log(G, `${p.name} покупает «${def.name}» за ${cost}${chips ? ` (${chips} чипсинами)` : ''}`);
  emit(G, { type: 'buy', pid: p.id, iid: c.iid, cid: c.cid, cost, chips, pile: 'market' });
  runAction(G, base, pid, { name: 'buyCard', args: [iid, payChips] }, E.takeFromMarket(G, p, c, 'discard'));
  return { ok: true };
}

/** Купить шальную магию из стопки */
function buyChaos(G: Game, pid: PlayerId): ActionOutcome {
  const p = activePlayerFor(G, pid);
  if (isOutcome(p)) return p;
  const turn = turnOf(G);
  if (!G.chaosPile.length) return fail('шальная магия кончилась');
  if (turn.power < RULES.chaosPrice) return fail('не хватает мощи');
  const base = cloneGame(G);
  const c = G.chaosPile.pop();
  if (!c) return fail('шальная магия кончилась');
  turn.power -= RULES.chaosPrice;
  turn.bought.push(c.iid);
  log(G, `${p.name} покупает шальную магию`);
  emit(G, { type: 'buy', pid: p.id, iid: c.iid, cid: c.cid, cost: RULES.chaosPrice, chips: 0, pile: 'chaos' });
  runAction(G, base, pid, { name: 'buyChaos', args: [] }, E.gainWithTriggers(G, p, c, { bought: chaosToHand(G) }));
  return { ok: true };
}

/** Конец хода */
function endTurn(G: Game, pid: PlayerId): ActionOutcome {
  const p = activePlayerFor(G, pid);
  if (isOutcome(p)) return p;
  runAction(G, cloneGame(G), pid, { name: 'endTurn', args: [] }, endTurnGen(G));
  return { ok: true };
}

function* endTurnGen(G: Game): Eff {
  const turn = turnOf(G);
  const p = player(G, turn.player);
  log(G, `${p.name} заканчивает ход`);
  emit(G, { type: 'endTurn', pid: p.id });
  // сброс руки
  p.discard.push(...p.hand);
  p.hand = [];
  // эффекты «в конце хода»
  p.handSize = H.handLimit(G, p, handSizeOf(G));
  turn.extraDraw += H.extraDraw(G, p);
  if (G.prizeOwner === p.id && !H.noPrizeBonus(p)) {
    gainChips(G, p, RULES.prizeChips, 'главный приз');
  }
  // сыгранные карты в сброс (карты с чужой колоды — владельцу)
  for (const c of [...p.inPlay]) {
    if (c.returnTo != null) {
      const owner = player(G, c.returnTo);
      delete c.returnTo;
      giveCard(G, p, owner, c, 'discard', { back: true });
    } else p.discard.push(c);
  }
  p.inPlay = [];
  // добор
  const n = turn.noDraw ? 0 : p.handSize + turn.extraDraw;
  drawCards(G, p, n);
  // проверка конца игры
  const needMain = marketSizeOf(G) - G.market.length;
  const needLeg = legendMarketSizeOf(G) - G.legendMarket.length;
  if (G.endTriggered || G.mainDeck.length < needMain || G.legendDeck.length < needLeg || !(G.ddkPile ?? []).length) {
    yield* finishGame(G);
    return;
  }
  startTurn(G, nextPlayerId(G));
  yield* beginTurn(G);
}

// ---------- конец игры ----------

/** Условные строки очков по составу карт owned: жетон свойства, Гусыня, Гикпиг, ЖДК, штраф/бонус вялых палочек */
function conditionalLines(G: Game, p: Player, owned: CardInst[]): ScoreLine[] {
  const lines: ScoreLine[] = [];
  const limpCount = owned.filter((c) => card(c).type === 'вялая палочка').length;
  const limpPositive = H.limpPositive(p, owned);
  const ctx: H.EndGameCtx = { owned, limpCount, limpPositive, legends: owned.filter((c) => H.isLegendFor(p, c)).length };
  lines.push(...H.endGameLines(G, p, ctx));
  if (limpCount) {
    // вялые палочки: штраф за каждую; пассив «приносят ПО, а не отнимают» (Виагрус) меняет знак; босс Король Похоти — без штрафа
    const per = limpPositive ? -RULES.limpPenalty : H.limpNoPenalty(p) ? 0 : RULES.limpPenalty;
    lines.push({ label: `Вялые палочки ×${limpCount}${limpPositive ? ' (в плюс)' : per === 0 ? ' (без штрафа)' : ''}`, value: per * limpCount });
  }
  return lines;
}

/** Бонус/штраф ПО, который карта c даёт игроку p сверх напечатанных ★ (жетон «+1 ПО за сокровище», Гусыня, Гикпиг,
 *  вялые палочки…): разница условных строк с картой и без неё. Для чужой карты (барахолка) — «если бы она была моей».
 *  labels — какие строки изменились (подсказка на карте) */
export function vpBonusOf(G: Game, p: Player, c: CardRef): { value: number; labels: string[] } {
  const owned = ownedCards(G, p);
  const mine = owned.some((x) => x.iid === c.iid);
  const withC = mine ? owned : [...owned, { iid: c.iid, cid: c.cid }];
  const without = mine ? owned.filter((x) => x.iid !== c.iid) : owned;
  const a = conditionalLines(G, p, withC);
  const b = conditionalLines(G, p, without);
  const sum = (ls: ScoreLine[]): number => ls.reduce((s, l) => s + l.value, 0);
  // напечатанные ★ вялой палочки в базу не входят — их заменяет строка штрафа; бонус показываем сверх напечатанных
  const printedSkipped = card(c).type === 'вялая палочка' ? card(c).vp ?? 0 : 0;
  const value = sum(a) - sum(b) - printedSkipped;
  if (!value) return { value: 0, labels: [] };
  // изменившиеся строки: сравниваем по значению с одноимённой строкой (строка палочек меняет текст вместе с числом)
  const key = (l: ScoreLine): string => l.label.replace(/ ×\d+/, '');
  const before = new Map(b.map((l) => [key(l), l.value]));
  const labels = a.filter((l) => before.get(key(l)) !== l.value).map((l) => key(l));
  return { value, labels };
}

export function scorePlayer(G: Game, p: Player): Score {
  const owned = ownedCards(G, p);
  const lines: Score['lines'] = [];
  let total = 0;
  let base = 0;
  for (const c of owned) {
    const d = card(c);
    if (d.type === 'вялая палочка') continue; // штраф за палочку считается отдельной строкой ниже
    base += d.vp ?? 0;
  }
  lines.push({ label: 'ПО на картах', value: base });
  total += base;
  for (const l of conditionalLines(G, p, owned)) {
    lines.push(l);
    total += l.value;
  }
  // жетоны дохлых колдунов: базовый штраф + звезда; уничтоженные в конце игры не считаются
  const alive = p.ddk.filter((id) => !H.ddkDestroyedAtEnd(G, p, ddkDef(id)));
  if (p.ddk.length) {
    const ddkTotal = alive.reduce((n, id) => n + ddkPenalty(ddkDef(id)), 0);
    lines.push({ label: `Жетоны дохлых колдунов ×${p.ddk.length}`, value: ddkTotal });
    total += ddkTotal;
  }
  if (p.loser) {
    lines.push({ label: 'Лошара', value: RULES.loserPenalty });
    total += RULES.loserPenalty;
  }
  // тай-брейк (правила, стр. 9): при равных ПО побеждает тот, у кого БОЛЬШЕ легенд, затем — у кого меньше ЖДК;
  // фамильяры при жетоне «фамильяры считаются легендами» идут в зачёт — это игроку на руку
  const legends = owned.filter((c) => H.isLegendFor(p, c)).length;
  return { total, lines, legends, ddk: alive.length };
}

/** Все победители партии: при полной ничьей (с v18) — несколько, иначе — один (или никого, пока партия идёт) */
export function winnersOf(G: Pick<Game, 'winner' | 'winners'>): PlayerId[] {
  return G.winners ?? (G.winner != null ? [G.winner] : []);
}

/** Итоговая таблица: по ПО; при равенстве — больше легенд выше; затем — меньше ЖДК (правила, стр. 9) */
export function rankScores(G: Game): ScoreEntry[] {
  const scores: ScoreEntry[] = G.players.map((p) => ({ pid: p.id, ...scorePlayer(G, p) }));
  return scores.sort((a, b) => b.total - a.total || b.legends - a.legends || a.ddk - b.ddk);
}

/** Почему кончилась игра (правила, стр. 9 и 14): нехватка карт на пополнение барахолок или пустая стопка ЖДК */
function endReasons(G: Game): string {
  const rs: string[] = [];
  const needMain = marketSizeOf(G) - G.market.length;
  const needLeg = legendMarketSizeOf(G) - G.legendMarket.length;
  if (G.mainDeck.length < needMain) rs.push(`в основной колоде не хватает карт пополнить барахолку (${G.mainDeck.length} из ${needMain})`);
  if (G.legendDeck.length < needLeg) rs.push(`в колоде легенд не хватает карт пополнить легендарную барахолку (${G.legendDeck.length} из ${needLeg})`);
  if (!(G.ddkPile ?? []).length) rs.push('стопка жетонов дохлых колдунов кончилась');
  if (!rs.length && G.endTriggered) rs.push('колода кончилась при пополнении барахолки');
  return rs.join('; ');
}

function* finishGame(G: Game, instantWinner: PlayerId | null = null): Eff {
  G.phase = 'ended';
  const scores = rankScores(G);
  G.scores = scores;
  const top = scores[0];
  if (!top) throw new Error('Нет игроков для подсчёта очков');
  G.winner = instantWinner ?? top.pid;
  // полная ничья (с v18): победа всем, кто равен первому по ПО, легендам и ЖДК
  const tied = instantWinner == null && sharedWin(G) ? scores.filter((s) => s.total === top.total && s.legends === top.legends && s.ddk === top.ddk) : [];
  if (tied.length > 1) G.winners = tied.map((s) => s.pid);
  const turnPid = G.turn ? G.turn.player : null;
  const reason = instantWinner != null ? 'Палочка-ушаталочка: немедленная победа' : endReasons(G);
  G.endInfo = { reason, turn: turnPid };
  if (instantWinner != null) log(G, `Палочка-ушаталочка: ${player(G, instantWinner).name} побеждает немедленно!`);
  if (reason) log(G, `Конец игры${turnPid != null ? ` в ход ${player(G, turnPid).name}` : ''}: ${reason}`);
  log(G, G.winners ? `Игра окончена! Ничья — побеждают: ${G.winners.map((id) => player(G, id).name).join(', ')}` : `Игра окончена! Победитель: ${player(G, G.winner).name}`);
  emit(G, { type: 'end', winner: G.winner, ...(G.winners ? { winners: G.winners } : {}), ...(reason ? { reason } : {}), ...(turnPid != null ? { turn: turnPid } : {}) });
}

// ---------- ручные корректировки (на случай карт с ручным применением) ----------
function manualAdjust(G: Game, pid: PlayerId, field: ManualField, delta: number): ActionOutcome {
  const p = player(G, pid);
  if (field === 'life') p.life = Math.max(0, Math.min(maxLife(p), p.life + delta));
  if (field === 'chips') {
    if (delta > 0) gainChips(G, p, delta, 'вручную');
    else spendChips(G, p, -delta, 'вручную');
  }
  if (field === 'power' && G.turn) G.turn.power = Math.max(0, G.turn.power + delta);
  if (field === 'loser') p.loser = !p.loser;
  log(G, `✎ Ручная правка: ${p.name} ${field} ${delta > 0 ? '+' : ''}${delta}`);
  return { ok: true };
}

function manualDraw(G: Game, pid: PlayerId, n: number): ActionOutcome {
  drawCards(G, player(G, pid), n);
  return { ok: true };
}

// ---------- единая точка действий ----------

/** Кому адресован активный запрос */
export function promptWho(G: Game): PlayerId | null {
  if (!G.prompt) return null;
  return G.prompt.who ?? (G.turn ? G.turn.player : null);
}

/**
 * Автоматика активного игрока после каждого действия, когда движок никого не ждёт:
 * - карты без атаки, не требующие выбора и не зависящие от момента (T.isAutoPlay), разыгрываются сами —
 *   правила разрешают играть простые карты все разом; добранные карты проверяются тем же циклом;
 *   исключение — пока на руке карта, для которой важен порядок (Вурдалакар: «если это первая сыгранная карта»)
 *   или состав руки (Шлем-тотем: уничтожить карту с руки) — см. T.holdsAutoPlay;
 * - с v13 автоматика ждёт и тогда, когда на столе постоянка, уничтожающая карты с руки (Волшебные отходы), а
 *   игрок в этом ходу ещё ничего не сыграл: первая сыгранная руками карта (или сама ☀ отходов) её запускает —
 *   см. tableHolds;
 * - самосрабатывающие активации ☀ (жетон «+1 чипсина», Суккулёндра) применяются, как только условие выполнено.
 *
 * Розыгрыш простых карт игрок может вернуть кнопкой «Отмена» (см. markAutoUndo): карты уходят обратно в руку,
 * и в этом ходу автоматика их больше не играет (turn.noAuto) — дальше он играет их сам и в своём порядке.
 */
function autoPlay(G: Game, hold = false): AutoPlayed | null {
  if (noAutoOnce) return null;
  const outer = autoPlaying;
  autoPlaying = true;
  try {
    return autoPlayLoop(G, hold);
  } finally {
    autoPlaying = outer;
  }
}

/** Что автоматика разыграла за игрока: снимок состояния ДО розыгрыша и сыгранные карты (для «Отмены») */
interface AutoPlayed {
  base: Game;
  played: Iid[];
}

/** Автоматика придержала карту с добором — действие, после которого это случилось, получит пометку h в журнале */
let heldChain = false;

/**
 * Карты с добором (T.revealsOnPlay) автоматика не играет совсем: добор показывает игроку закрытые карты, и «Отмена»
 * после него невозможна (иначе можно было бы переиграть, уже зная карты). Игрок играет их сам, когда сочтёт нужным —
 * после «Отмены» автоматики, уничтожения хлама с руки и т.п. Придержанные карты запоминаются в turn.noAuto (как
 * возвращённые «Отменой»). hold — разрешено ли придерживать: у записей журнала без пометки h (старых) автоматика
 * играла и карты с добором — их воспроизводим как раньше
 */
function autoPlayLoop(G: Game, hold: boolean): AutoPlayed | null {
  let auto: AutoPlayed | null = null;
  for (let guard = 0; guard < 40; guard++) {
    if (G.phase !== 'play' || G.prompt || busy(G) || !G.turn) return auto;
    const p = player(G, G.turn.player);
    // удержание карт с добором переживает отмену и перезагрузку партии (запоминаем в turn.noAuto)
    if (hold) {
      const held = p.hand.filter((x) => T.isAutoPlay(x.cid) && T.revealsOnPlay(G, x.cid) && !G.turn!.noAuto?.includes(x.iid)).map((x) => x.iid);
      if (held.length) {
        G.turn.noAuto = [...(G.turn.noAuto ?? []), ...held];
        heldChain = true;
      }
    }
    const noAuto = G.turn.noAuto;
    // карту, которую игрок уже вернул в руку «Отменой», автоматика второй раз не играет; добор — не при возможности
    // уничтожить карты с руки (иначе «Отмена» закроется, и хлам, сыгранный автоматикой, в руку не вернуть)
    const careful = trashAvailable(G, p);
    const plain = T.holdsAutoPlay(p.hand) || tableHolds(G, p) ? null
      : p.hand.find((x) => T.isAutoPlay(x.cid) && !noAuto?.includes(x.iid) && !(careful && T.revealsOnPlay(G, x.cid)));
    if (plain) {
      // снимок до первого розыгрыша: игрок вправе вернуть сыгранное автоматикой в руку (см. markAutoUndo).
      // Примеркам бота и ходам ботов он не нужен — отменяет только человек, и только свой ход
      const base: Game | null = !auto && !G.probe && !p.bot ? cloneGame(G) : null;
      if (!playCard(G, p.id, plain.iid).ok) return auto;
      if (base) auto = { base, played: [plain.iid] };
      else auto?.played.push(plain.iid);
      // страховка от ошибки в реестре: автоматическая карта не должна задавать вопросов (симуляции ловят ⚠)
      if (G.prompt) log(G, `⚠ автоматика: «${card(plain).name}» задала вопрос — карта должна быть в MANUAL_PLAY`);
      continue;
    }
    const tok = H.tokenActivations(G, p).find((a) => a.auto && a.enabled !== false);
    const c = tok ? null : controlled(G, p).find((x) => T.AUTO_ACTIVATE.has(x.cid) && canActivate(G, x));
    if (!tok && !c) return auto;
    if (!activateCard(G, p.id, tok ? tok.id : c ? c.iid : '').ok) return auto;
  }
  return auto;
}

/** Есть чем уничтожить карты с руки: такая карта на руке (сыграет — будет ☀) или на столе с доступной ☀ (с v17) */
function trashAvailable(G: Game, p: Player): boolean {
  if (!holdRevealsForTrash(G)) return false;
  return p.hand.some((x) => T.HAND_TRASHERS.has(x.cid)) || controlled(G, p).some((x) => T.HAND_TRASHERS.has(x.cid) && canActivate(G, x));
}

/** Постоянка «уничтожь карты с руки» держит автоматику, пока её ☀ доступна и в этом ходу ещё ничего не сыграно */
function tableHolds(G: Game, p: Player): boolean {
  if (!holdForTableTrash(G) || !G.turn || G.turn.played.length) return false;
  return p.permanents.some((x) => T.TABLE_HAND_TRASH.has(x.cid) && canActivate(G, x));
}

/**
 * Автоматика сыграла простые карты за игрока — и это тоже можно отменить: карту могло быть нужно сыграть в
 * другом порядке, оставить в руке или не играть вовсе. Точка отката — состояние до розыгрыша; отмена
 * вернёт карты в руку и запретит автоматике играть их снова в этом ходу (см. cancel → turn.noAuto).
 * Если розыгрыш успел что-то раскрыть (добор), возврат стал бы подглядыванием — точки отката нет, но причина
 * остаётся в undoWhy: игрок должен видеть правду, а не пустоту вместо кнопки.
 */
function markAutoUndo(G: Game, auto: AutoPlayed): void {
  if (G.phase !== 'play' || !G.turn || G.prompt || G.pending) return;
  const pid = G.turn.player;
  const base = auto.base;
  // точка отката самого действия (с v20): авто-розыгрыш встаёт перед ней — «Отмена» сначала вернёт его
  const prevMark = G.undoAt;
  const cached = undos.get(undoKey(G));
  const prev = prevMark && cached && cached.at === prevMark.at && !cached.auto ? cached : undefined;
  if (base.turn?.player !== pid) return; // ход успел перейти другому — отменять нечего
  if (!clean(G, base)) {
    G.undoWhy = 'foreign';
    return;
  }
  if ((G.softSeq ?? 0) !== (base.softSeq ?? 0)) {
    G.undoWhy = 'autodraw';
    return;
  }
  const what = auto.played.length > 1 ? 'авто-розыгрыш карт' : 'авто-розыгрыш карты';
  const at = G.journal?.length ?? 0;
  undoSet(G, { base, pid, what, at, lockSeq: base.lockSeq ?? 0, rngS: base.rng?.s ?? 0, softSeq: base.softSeq ?? 0, auto: true, ...(prev ? { prev } : {}) });
  G.undoAt = { at, lockSeq: base.lockSeq ?? 0, rngS: base.rng?.s ?? 0, softSeq: base.softSeq ?? 0, pid, what, auto: true };
  delete G.undoWhy;
}

/** Выполнить действие от имени игрока pid: проверяет права и применимость, возвращает результат */
/**
 * hold — придерживать ли автоматикой карты с добором. В живой игре да; воспроизводя запись, берут из неё
 * (applyEntry): у старых записей пометки h нет, и они играются как раньше
 */
export function applyAction(G: Game, pid: PlayerId, a: Action, hold = true): ActionOutcome {
  const r = dispatch(G, pid, a);
  if (r.ok) {
    // журнал: партия воспроизводится с нуля как createGame({ seed, players }) + те же действия
    const entry: JournalEntry = { pid, a };
    (G.journal ??= []).push(entry);
    // точка отката привязывается к позиции в журнале — теперь она известна
    if (pendingUndo && a.name !== 'endTurn') {
      const entry = { ...pendingUndo, at: G.journal.length };
      undoSet(G, entry);
      G.undoAt = { at: entry.at, lockSeq: entry.lockSeq, rngS: entry.rngS, softSeq: entry.softSeq, pid: entry.pid, what: entry.what };
    // отмена возвращает состояние снимка целиком — метку в нём не трогаем (иначе состояние разойдётся
    // со снимком, что ловит sim); новое содержательное действие метку перепишет само
    } else if (!autoPlaying && a.name !== 'cancel') delete G.undoAt;
    pendingUndo = null;
    // отменён авто-розыгрыш (v20): следующий шаг «Отмены» — само действие, привязанное к записи отмены
    if (a.name === 'cancel' && restoreUndo) {
      const { mark, base } = restoreUndo;
      const at = G.journal.length;
      const src = mark.src ?? mark.at;
      G.undoAt = { ...mark, at, src };
      if (base) undoSet(G, { base, pid: mark.pid, what: mark.what, at, lockSeq: mark.lockSeq, rngS: mark.rngS, softSeq: mark.softSeq });
    }
    restoreUndo = null;
    if (a.name === 'endTurn') {
      undos.delete(undoKey(G)); // добор новой руки уже раскрыл карты
      delete G.undoAt;
    }
    heldChain = false;
    const auto = autoPlay(G, hold);
    if (heldChain) entry.h = 1;
    // точку отката на авто-розыгрыш ставим, только если само действие откатить уже нельзя: его откат вернул бы
    // и сыгранное автоматикой. После отмены новой точки не ставим — иначе «Отмена» отменяла бы сама себя
    // (с v20 — и поверх точки отката самого действия, отдельным шагом: см. markAutoUndo, cancel)
    const own = !!G.undoAt && !G.undoAt.auto && G.undoAt.at === G.journal.length && G.undoAt.pid === G.turn?.player;
    if (auto && (!G.undoAt || own) && a.name !== 'cancel') markAutoUndo(G, auto);
  } else pendingUndo = null;
  return r;
}

/** Применить запись журнала так же, как она была сыграна (с пометкой h цепочку добора придерживают) */
export function applyEntry(G: Game, e: JournalEntry): ActionOutcome {
  return applyAction(G, e.pid, e.a, !!e.h);
}

function dispatch(G: Game, pid: PlayerId, a: Action): ActionOutcome {
  switch (a.name) {
    case 'setupChoose': return setupChoose(G, pid, a.args[0]);
    case 'setupFamiliar': return setupFamiliar(G, pid, a.args[0]);
    case 'setupThird': return setupThird(G, pid, a.args[0]);
    case 'answer': {
      if (!G.prompt) return fail('нет запроса');
      if (promptWho(G) !== pid) return fail('не твой запрос');
      answer(G, a.args[0]);
      return { ok: true };
    }
    case 'playCard': return playCard(G, pid, a.args[0]);
    case 'activateCard': return activateCard(G, pid, a.args[0]);
    case 'buyCard': return buyCard(G, pid, a.args[0], a.args[1]);
    case 'buyChaos': return buyChaos(G, pid);
    case 'endTurn': return endTurn(G, pid);
    case 'cancel': return cancel(G, pid, a.args[0] === 'step');
    case 'manualAdjust': return manualAdjust(G, a.args[0], a.args[1], a.args[2]);
    case 'manualDraw': return manualDraw(G, a.args[0], a.args[1]);
  }
}

/**
 * Мотив выбора цели ботом — заметка в журнал и лента событий: за столом видно, почему бот бьёт именно этого.
 * Пишется только в настоящей партии (в примерках G.probe) и только когда выбор был («добивает», «мстит за урон»,
 * «бьёт лидера», у психа — «просто хочется»); человеческие ответы и выборы без вреда не комментируются.
 */
function botTargetNote(G: Game, pid: PlayerId, ans: PromptAnswer): void {
  const pr = G.prompt;
  if (G.probe || !pr || pr.type !== 'choosePlayer' || typeof ans !== 'number' || ans === pid) return;
  const me = player(G, pid);
  if (!me.bot || (!pr.damage && !pr.harm)) return;
  const rivals = pr.candidates.filter((c) => c !== pid);
  if (rivals.length < 2) return; // выбора не было — нечего объяснять
  const target = player(G, ans);
  const dmg = pr.damage?.[ans] ?? 0;
  const grudge = me.grudge?.[ans] ?? 0;
  const topGrudge = Math.max(...rivals.map((c) => me.grudge?.[c] ?? 0));
  const topVp = Math.max(...rivals.map((c) => scorePlayer(G, player(G, c)).total));
  let kind: string | null = null;
  let why = '';
  if (dmg > 0 && target.life <= dmg) [kind, why] = ['kill', `добивает (${target.life}❤ при ${dmg} урона)`];
  else if (me.botStyle === 'vengeful' && grudge >= 3 && grudge >= topGrudge) [kind, why] = ['grudge', `мстит за ${grudge} полученного урона`];
  else if (scorePlayer(G, target).total >= topVp) [kind, why] = ['leader', 'бьёт лидера'];
  else if (me.botStyle === 'maniac') [kind, why] = ['whim', 'просто хочется'];
  if (!kind) return;
  const key = `${ans}:${kind}`;
  if (me.noted === key) return; // тот же мотив по той же цели — не спамим, пока не сменится
  me.noted = key;
  E.note(G, me, `цель — ${target.name}: ${why}`);
}

/** Как applyAction, но неуспех — это баг вызывающего: бросает ошибку */
export function mustApply(G: Game, pid: PlayerId, a: Action): void {
  const r = applyAction(G, pid, a);
  if (!r.ok) throw new Error(`${a.name}: ${r.error}`);
}

/** Подготовка игрока одним вызовом (тесты, скрипты): жетон, затем фамильяр — как требует версия правил партии */
export function mustSetup(G: Game, pid: PlayerId, traitId: string, familiar: CardId | null): void {
  if (!stepwiseSetup(G)) {
    mustApply(G, pid, { name: 'setupChoose', args: [{ traitId, familiar }] });
    return;
  }
  mustApply(G, pid, { name: 'setupChoose', args: [{ traitId, familiar: null }] });
  if (setupDone(player(G, pid))) return;
  if (!familiar) throw new Error('setupFamiliar: не указан фамильяр');
  mustApply(G, pid, { name: 'setupFamiliar', args: [familiar] });
}

/** Конфигурация игроков для воспроизведения партии с нуля */
export function replayPlayers(G: Pick<Game, 'players'>): GameConfig['players'] {
  return G.players.map((p) => ({ name: p.name, bot: p.bot, ...(p.botLevel ? { botLevel: p.botLevel } : {}), ...(p.botStyle ? { botStyle: p.botStyle } : {}), ...(p.boss ? { boss: p.boss } : {}) }));
}

export interface ReplaySpec {
  seed: number;
  players: GameConfig['players'];
  rebalance?: boolean;
  mutators?: readonly MutatorId[];
  journal: readonly JournalEntry[];
}

/** Флаги партии для реплея/записи: режим ребаланса и мутаторы — ровно те, с которыми партия создавалась */
export function gameFlags(G: { rebalance?: boolean; mutators?: readonly MutatorId[] }): { rebalance?: boolean; mutators?: MutatorId[] } {
  return { ...(G.rebalance ? { rebalance: true } : {}), ...(G.mutators?.length ? { mutators: [...G.mutators] } : {}) };
}

/**
 * Воспроизвести журнал на версии правил v: createGame с тем же сидом и игроками + действия до until (не включая).
 * Действие, которое не легло, — расхождение правил с записью: failAt — его номер, error — причина.
 */
export function replayJournal(spec: ReplaySpec, v: number, until = Infinity, onStep?: (G: Game, e: JournalEntry) => void): { G: Game; failAt: number; error: string } {
  const G = createGame({ seed: spec.seed, players: spec.players, ...gameFlags(spec), rulesVersion: v });
  const n = Math.min(until, spec.journal.length);
  for (let i = 0; i < n; i++) {
    const e = spec.journal[i];
    if (!e) break;
    let r: ActionOutcome;
    try {
      r = applyEntry(G, e);
    } catch (x) {
      r = fail(x instanceof Error ? x.message : String(x));
    }
    if (!r.ok) return { G, failAt: i, error: r.error };
    onStep?.(G, e);
  }
  return { G, failAt: -1, error: '' };
}

/** Версии, которые стоит перебирать для партии: обычной партии важна только правка 6 (жизни лошары) */
export function candidateVersions(rebalance: boolean): number[] {
  const all: number[] = [];
  for (let v = RULES_VERSION; v >= RULES_VERSION_MIN; v--) all.push(v);
  return rebalance ? all : all.filter((v) => v === RULES_VERSION || v === 5);
}

/**
 * Отпечаток состояния для сверки реплея с сохранённой партией: всё, что определяет дальнейшую игру
 * (карты по зонам в точном порядке, жизни, чипсины, жетоны, барахолка, ход). Журнал, лог и события не входят.
 */
export function stateSignature(G: Game): string {
  const ids = (cs: readonly CardInst[]): string => cs.map((c) => c.iid).join(',');
  return JSON.stringify({
    phase: G.phase,
    turn: G.turn ? [G.turn.player, G.turn.power] : null,
    prize: G.prizeOwner,
    players: G.players.map((p) => [p.life, p.chips, p.loser, p.traitId, ids(p.hand), ids(p.deck), ids(p.discard), ids(p.permanents), ids(p.inPlay), ids(p.familiars), p.ddk.join(',')]),
    market: ids(G.market), legend: ids(G.legendMarket), main: G.mainDeck.length, legends: G.legendDeck.length,
    chaos: G.chaosPile.length, limp: G.limpPile.length, trash: ids(G.trash), chips: G.chipsSupply,
    rng: G.rng?.s ?? null,
  });
}

/**
 * Подобрать версию правил партии, сохранённой до появления версий: журнал воспроизводится на каждой версии от
 * новой к старой, и версия, на которой итог совпал с сохранённым состоянием карта в карту, — и есть версия
 * партии (реплей детерминирован, совпадение — доказательство). null — ни одна не сошлась: партия захватила
 * правку, которой в истории версий нет, или продолжалась через обновления.
 */
export function detectRulesVersion(G: Game): number | null {
  const journal = G.journal ?? [];
  if (G.seed == null || !journal.length) return null;
  const spec: ReplaySpec = { seed: G.seed, players: replayPlayers(G), ...gameFlags(G), journal };
  const target = stateSignature(G);
  for (const v of candidateVersions(!!G.rebalance)) {
    const r = replayJournal(spec, v);
    if (r.failAt >= 0) continue;
    if (stateSignature(r.G) === target) return v;
  }
  return null;
}

/**
 * Партия без версии (сохранена до их появления): подобрать её по журналу и запомнить в состоянии — с этого
 * момента движок играет её по этим правилам. Не подобралась — считается текущей (как играла бы и раньше),
 * но об этом остаётся отметка: реплей журнала (шаг назад) на ней невозможен.
 */
export function ensureRulesVersion(G: Game): void {
  if (G.rulesVersion != null) return;
  const v = detectRulesVersion(G);
  if (v != null) G.rulesVersion = v;
  else {
    G.rulesVersion = RULES_VERSION;
    G.undoWhy = 'legacy';
  }
  applyRebalance(!!G.rebalance, rv(G));
}

/**
 * Восстановление после загрузки: счётчик iid и незавершённый эффект. Эффект переигрывается со снимка:
 * состояние заменяется результатом переигрывания (он детерминирован и совпадает с сохранённым).
 */
export function restoreRuntime(G: Game): Game {
  applyRebalance(!!G.rebalance, rv(G)); // загрузка минует createGame — цены режима надо выставить по партии
  const pending = G.pending;
  if (!pending) {
    setIidCounter(maxIid(G) + 1);
    G.prompt = null;
    // сохранение старой версии могло застать руку с неразыгранными простыми картами — доигрываем автоматику
    autoPlay(G, false);
    return G;
  }
  const g = cloneGame(pending.base);
  setIidCounter(maxIid(g) + 1);
  mustApply(g, pending.pid, pending.action);
  for (const ans of pending.answers) answer(g, ans);
  // журнал сохранённого состояния полон (ответы внутри эффекта в нём уже есть), снимок — нет
  if (G.journal) g.journal = G.journal.slice();
  return g;
}

/** Копия состояния с живым (переигранным) незавершённым эффектом — для «примерок» бота */
export function forkGame(G: Game): Game {
  if (!G.pending) return cloneGame(G);
  return restoreRuntime(JSON.parse(JSON.stringify(G)) as Game);
}

export { E, T, H, CHAOS_CARD };
